import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

class HeaderComponent extends HTMLElement {
  #abort;
  #observer;
  #scrollTarget;
  #active;
  #hoverTimer;
  #closeTimer;
  #frame;
  #lastScroll = 0;
  #drawer;
  #drawerOpen = false;
  #drawerTimer;
  #panelStack = [];
  #editorBlock;

  connectedCallback() {
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#drawer = this.querySelector('[data-nav-drawer]');
    this.#syncMenuTargets();
    this.addEventListener('click', this.#click, options);
    this.addEventListener('pointerover', this.#pointerOver, options);
    this.addEventListener('pointerout', this.#pointerOut, options);
    this.addEventListener('keydown', this.#keydown, options);
    this.addEventListener('focusout', this.#focusOut, options);
    this.addEventListener('click', this.#utilityClick, { ...options, capture: true });
    this.#drawer?.addEventListener('change', this.#localizationChange, options);
    document.addEventListener('pointerdown', this.#outside, options);
    document.addEventListener('focusin', this.#focusOutsideDrawer, options);
    document.addEventListener('shopify:block:select', this.#selectBlock, options);
    document.addEventListener('shopify:block:deselect', this.#deselectBlock, options);
    document.addEventListener('dialog:open', this.#otherSurface, options);
    document.addEventListener('theme-drawer:open', this.#otherSurface, options);
    this.#observer = new ResizeObserver(this.#schedule);
    [this, this.querySelector('.ww-header__row'), this.querySelector('.ww-desktop-nav'), this.querySelector('.ww-header__logo'), this.querySelector('.ww-header__utilities'), document.querySelector('#header-group')].filter(Boolean).forEach((element) => this.#observer.observe(element));
    scrollContainerMediaQuery.addEventListener('change', this.#bindScroll, options);
    window.addEventListener('resize', this.#schedule, options);
    this.#bindScroll();
    document.fonts.ready.then(() => { if (this.isConnected) this.#schedule(); });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#hoverTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#drawerTimer);
    document.documentElement.classList.remove('ww-navigation-locked');
  }

  refreshNavigation = () => this.#schedule();

  #syncMenuTargets() {
    const megaPanels = new Map();
    this.querySelectorAll('.ww-nav-panel--mega[data-menu-key]').forEach((panel) => {
      if (!megaPanels.has(panel.dataset.menuKey)) megaPanels.set(panel.dataset.menuKey, panel);
    });
    this.querySelectorAll('[data-nav-key]').forEach((item) => {
      item.dataset.fallbackPanel ||= item.dataset.navItem;
      const panel = megaPanels.get(item.dataset.navKey);
      const targetId = panel?.id || item.dataset.fallbackPanel;
      item.dataset.navItem = targetId;
      const toggle = item.querySelector('[data-panel-toggle]');
      if (toggle) {
        toggle.dataset.panelToggle = targetId;
        toggle.setAttribute('aria-controls', targetId);
      }
    });
    const mobileTemplates = new Map();
    this.querySelectorAll('template[data-mobile-menu-template]').forEach((template) => {
      if (!mobileTemplates.has(template.dataset.mobileMenuTemplate)) {
        mobileTemplates.set(template.dataset.mobileMenuTemplate, template);
      }
    });
    this.querySelectorAll('[data-mobile-menu-key]').forEach((branch) => {
      const template = mobileTemplates.get(branch.dataset.mobileMenuKey);
      if (!template) return;
      branch.dataset.mobileBlock = template.dataset.menuBlock;
      branch.replaceChildren(template.content.cloneNode(true));
    });
  }

  #bindScroll = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#schedule, { passive: true });
    this.#schedule();
  };

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const row = this.querySelector('.ww-header__row');
    const nav = this.querySelector('.ww-desktop-nav');
    const logo = this.querySelector('.ww-header__logo');
    const utilities = this.querySelector('.ww-header__utilities');
    if (!row || !nav || !logo || !utilities) return;
    const styles = getComputedStyle(row);
    const gap = parseFloat(styles.columnGap) || 0;
    const available = row.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    const navWidth = nav.scrollWidth;
    const utilityChildren = [...utilities.children];
    const utilityGap = parseFloat(getComputedStyle(utilities).columnGap) || 0;
    const utilityWidth = utilityChildren.reduce((width, child) => width + child.getBoundingClientRect().width, 0) + Math.max(0, utilityChildren.length - 1) * utilityGap;
    const logoWidth = logo.scrollWidth;
    const required = this.dataset.layout === 'left' ? navWidth + logoWidth + utilityWidth + gap * 2 : Math.max(navWidth, utilityWidth) * 2 + logoWidth + gap * 2;
    const mode = window.innerWidth < 750 || required > available ? 'drawer' : 'menu';
    if (mode !== this.dataset.menuStyle) {
      this.closePanel();
      if (mode === 'menu') this.closeDrawer();
      this.dataset.menuStyle = mode;
    }
    const scrollTop = getScrollTop();
    const moved = scrollTop !== this.#lastScroll;
    const isUp = moved ? scrollTop < this.#lastScroll : this.dataset.scrollDirection === 'up';
    this.toggleAttribute('data-scrolled', scrollTop > 40);
    if (this.hasAttribute('sticky')) {
      this.dataset.stickyState = scrollTop <= 40 ? 'inactive' : this.getAttribute('sticky') === 'scroll-up' && !isUp && scrollTop > this.offsetHeight && !this.#active && !this.#drawerOpen ? 'idle' : 'active';
    }
    this.dataset.scrollDirection = scrollTop <= 40 ? 'none' : isUp ? 'up' : 'down';
    const transparent = this.hasAttribute('transparent') && scrollTop <= 40 && !this.#active && !this.#drawerOpen;
    this.toggleAttribute('data-transparent-active', transparent);
    this.dataset.logoTone = (transparent ? this.dataset.transparentInverse : this.dataset.solidInverse) === 'true' ? 'inverse' : 'normal';
    this.#lastScroll = scrollTop;
    document.body.style.setProperty('--header-height', this.offsetHeight + 'px');
    const group = document.querySelector('#header-group');
    if (group) {
      let height = 0;
      for (const child of group.children) {
        if (this.hasAttribute('transparent') && child.contains(this)) continue;
        height += child.getBoundingClientRect().height;
      }
      document.body.style.setProperty('--header-group-height', height + 'px');
    }
    this.#setDrawerTop();
    this.style.setProperty('--ww-panel-height', Math.max(120, window.innerHeight - this.getBoundingClientRect().bottom - 16) + 'px');
  };

  openPanel(id) {
    if (this.dataset.menuStyle !== 'menu') return;
    const panel = document.getElementById(id);
    if (!panel || !this.contains(panel)) return;
    clearTimeout(this.#closeTimer);
    if (this.#active !== panel) this.closePanel();
    panel.hidden = false;
    this.#active = panel;
    this.toggleAttribute('data-nav-open', true);
    const toggle = [...this.querySelectorAll('[data-panel-toggle]')].find((button) => button.dataset.panelToggle === id);
    toggle?.setAttribute('aria-expanded', 'true');
    if (panel.classList.contains('ww-nav-panel--dropdown') && toggle) {
      const left = toggle.parentElement.getBoundingClientRect().left - this.getBoundingClientRect().left;
      panel.style.setProperty('--ww-dropdown-left', Math.max(0, Math.min(left, this.clientWidth - panel.offsetWidth)) + 'px');
    }
    this.#schedule();
  }

  closePanel(restore = false) {
    clearTimeout(this.#hoverTimer);
    clearTimeout(this.#closeTimer);
    const active = this.#active;
    if (!active) return;
    const toggle = [...this.querySelectorAll('[data-panel-toggle]')].find((button) => button.dataset.panelToggle === active.id);
    active.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    if (restore) toggle?.focus();
    this.#active = null;
    this.removeAttribute('data-nav-open');
    this.#schedule();
  }

  get isDrawerOpen() { return this.#drawerOpen; }

  openDrawer() {
    if (!this.#drawer || this.#drawerOpen) return;
    this.closePanel();
    clearTimeout(this.#drawerTimer);
    this.#drawerOpen = true;
    this.#update();
    this.#drawer.hidden = false;
    this.#drawer.getBoundingClientRect();
    this.#drawer.toggleAttribute('data-open', true);
    this.toggleAttribute('data-drawer-open', true);
    document.documentElement.classList.add('ww-navigation-locked');
    this.#drawerToggle?.setAttribute('aria-expanded', 'true');
    this.#schedule();
  }

  closeDrawer(restore = false) {
    if (!this.#drawer || !this.#drawerOpen) return;
    const hadFocus = this.#drawer.contains(document.activeElement);
    this.#drawerOpen = false;
    this.#drawer.removeAttribute('data-open');
    this.removeAttribute('data-drawer-open');
    document.documentElement.classList.remove('ww-navigation-locked');
    this.#drawerToggle?.setAttribute('aria-expanded', 'false');
    if (restore || hadFocus) this.#drawerToggle?.focus({ preventScroll: true });
    clearTimeout(this.#drawerTimer);
    this.#drawerTimer = setTimeout(() => {
      if (this.#drawerOpen) return;
      this.#drawer.hidden = true;
      this.#resetDrawerPanels();
    }, this.#motionDuration);
    this.#schedule();
  }

  get #drawerToggle() { return this.querySelector('[data-drawer-toggle]'); }

  get #motionDuration() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450;
  }

  #setDrawerTop() {
    if (!this.#drawerOpen) return;
    const bottom = this.querySelector('.ww-header__row')?.getBoundingClientRect().bottom ?? 0;
    this.style.setProperty('--ww-drawer-top', Math.max(0, bottom) + 'px');
  }

  #drawerPanel(id) {
    const panel = id && document.getElementById(id);
    return panel && this.#drawer?.contains(panel) ? panel : null;
  }

  #setPanelState(panel, state) {
    if (state) panel.dataset.state = state;
    else panel.removeAttribute('data-state');
    panel.inert = state !== 'active';
  }

  showDrawerPanel(id, trigger) {
    const panel = this.#drawerPanel(id);
    const current = this.#drawerPanel(this.#panelStack.at(-1)) || this.#drawer?.querySelector('[data-drawer-panel][data-state="active"]');
    if (!panel || panel === current) return;
    if (current) this.#setPanelState(current, 'behind');
    this.#setPanelState(panel, 'active');
    panel.scrollTop = 0;
    trigger?.setAttribute('aria-expanded', 'true');
    this.#panelStack.push(panel.id);
    panel.querySelector('[data-drawer-back]')?.focus({ preventScroll: true });
  }

  #backDrawerPanel() {
    if (this.#panelStack.length < 2) return false;
    const current = this.#drawerPanel(this.#panelStack.pop());
    const previous = this.#drawerPanel(this.#panelStack.at(-1));
    if (!current || !previous) return false;
    this.#setPanelState(current, null);
    this.#setPanelState(previous, 'active');
    const trigger = previous.querySelector('[data-drawer-next="' + CSS.escape(current.id) + '"]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus({ preventScroll: true });
    return true;
  }

  #resetDrawerPanels() {
    const panels = this.#drawer?.querySelectorAll('[data-drawer-panel]') ?? [];
    panels.forEach((panel, index) => this.#setPanelState(panel, index === 0 ? 'active' : null));
    this.#drawer?.querySelectorAll('[data-drawer-next]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.#panelStack = panels.length ? [panels[0].id] : [];
  }

  #openDrawerBranch(branch) {
    const panel = branch?.querySelector('[data-drawer-panel]');
    if (!panel) return;
    this.#resetDrawerPanels();
    this.openDrawer();
    const trigger = this.#drawer.querySelector('[data-drawer-next="' + CSS.escape(panel.id) + '"]');
    this.showDrawerPanel(panel.id, trigger);
  }

  #click = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest('[data-panel-toggle]');
    if (toggle) {
      this.#editorBlock = null;
      this.#active?.id === toggle.dataset.panelToggle ? this.closePanel() : this.openPanel(toggle.dataset.panelToggle);
    } else if (target.closest('[data-drawer-toggle]')) {
      if (this.#drawerOpen) this.closeDrawer();
      else {
        this.#resetDrawerPanels();
        this.openDrawer();
        if (event.detail === 0) this.#drawer.querySelector('[data-drawer-panel] a, [data-drawer-panel] button')?.focus({ preventScroll: true });
      }
    } else if (target.closest('[data-drawer-close]')) this.closeDrawer();
    else if (target.closest('[data-drawer-next]')) {
      const button = target.closest('[data-drawer-next]');
      this.showDrawerPanel(button.dataset.drawerNext, button);
    } else if (target.closest('[data-drawer-back]')) this.#backDrawerPanel();
  };
  #utilityClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.ww-header__utilities')) return;
    this.closePanel();
    this.closeDrawer();
  };
  #localizationChange = (event) => {
    const form = event.target.closest?.('[data-drawer-localization]');
    if (form && event.target.matches('select')) form.requestSubmit();
  };
  #focusOutsideDrawer = (event) => {
    if (this.#drawerOpen && event.target instanceof Node && !this.contains(event.target)) this.closeDrawer();
  };
  #pointerOver = (event) => {
    if (event.pointerType !== 'mouse' || this.#editorBlock) return;
    const item = event.target.closest('[data-nav-item]');
    const panel = event.target.closest('[data-nav-panel]');
    if (panel || item) clearTimeout(this.#closeTimer);
    if (item && !item.contains(event.relatedTarget)) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = setTimeout(() => this.openPanel(item.dataset.navItem), 150);
    }
  };
  #pointerOut = (event) => {
    if (event.pointerType !== 'mouse' || this.#editorBlock) return;
    const zone = event.target.closest('[data-nav-item], [data-nav-panel]');
    if (!zone || zone.contains(event.relatedTarget)) return;
    clearTimeout(this.#hoverTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (!this.#active?.contains(document.activeElement)) this.closePanel();
    }, 180);
  };
  #outside = (event) => { if (!this.contains(event.target)) this.closePanel(); };
  #focusOut = () => {
    queueMicrotask(() => {
      if (this.#editorBlock || !this.#active) return;
      const focus = document.activeElement;
      const owner = this.querySelector('[data-nav-item="' + CSS.escape(this.#active.id) + '"]');
      if (!this.#active.contains(focus) && !owner?.contains(focus)) this.closePanel();
    });
  };
  #keydown = (event) => {
    if (event.key === 'Escape' && this.#drawerOpen) {
      event.preventDefault();
      if (!this.#backDrawerPanel()) this.closeDrawer(true);
      return;
    }
    if (event.key === 'Escape') { this.#editorBlock = null; this.closePanel(true); }
    if (event.key === 'Tab' && this.#active) {
      const toggle = this.querySelector('[data-panel-toggle="' + CSS.escape(this.#active.id) + '"]');
      const links = [...this.#active.querySelectorAll('a[href], button:not([disabled])')];
      if (!event.shiftKey && event.target === toggle && links.length) {
        event.preventDefault();
        links[0].focus();
      } else if (event.shiftKey && event.target === links[0]) {
        event.preventDefault();
        toggle?.focus();
      } else if (!event.shiftKey && event.target === links.at(-1)) {
        event.preventDefault();
        const next = toggle?.closest('li')?.nextElementSibling?.querySelector('a, button') || this.querySelector('.ww-header__utilities button');
        this.closePanel();
        next?.focus();
      }
    }
    if (event.key === 'ArrowDown') {
      const toggle = event.target.closest('[data-panel-toggle]');
      if (toggle) {
        event.preventDefault();
        this.openPanel(toggle.dataset.panelToggle);
        this.#active?.querySelector('a, button')?.focus();
      }
    }
  };
  #selectBlock = (event) => {
    const id = event.detail?.blockId;
    if (!id || !this.querySelector('[data-menu-block="' + CSS.escape(id) + '"]')) return;
    this.#editorBlock = id;
    if (this.dataset.menuStyle === 'drawer') {
      this.#openDrawerBranch(this.querySelector('[data-mobile-block="' + CSS.escape(id) + '"]'));
    } else this.openPanel('MegaMenu-' + id);
  };
  #deselectBlock = () => { this.#editorBlock = null; this.closePanel(); this.closeDrawer(); };
  #otherSurface = () => { this.closePanel(); this.closeDrawer(); };
}
if (!customElements.get('header-component')) customElements.define('header-component', HeaderComponent);
